import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { commissionsService, itinerariesService, suppliersService } from '../../services';
import './CommissionForm.css';

const CommissionForm = ({ onClose, onSuccess, selectedItinerary = null }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [itineraries, setItineraries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    itinerary_id: selectedItinerary?.id || '',
    first_name: user?.firstName || user?.first_name || '',
    last_name: user?.lastName || user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    reservation_number: '',
    guest_first_name: '',
    guest_last_name: '',
    supplier: '',
    booking_date: new Date().toISOString().split('T')[0],
    travel_date: '',
    gross_commission: '',
    notes: ''
  });

  useEffect(() => {
    fetchItineraries();
    fetchSuppliers();
  }, []);

  const fetchItineraries = async () => {
    try {
      const data = await itinerariesService.getAll();
      // Only show completed itineraries that don't have commissions yet
      const completedItineraries = data.filter(itinerary => 
        itinerary.status === 'completed' && 
        new Date(itinerary.travel_end_date) < new Date()
      );
      setItineraries(completedItineraries);
    } catch (error) {
      console.error('Error fetching itineraries:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await suppliersService.getAll();
      setSuppliers(data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.reservation_number) {
      newErrors.reservation_number = 'Reservation number is required';
    }
    if (!formData.guest_first_name) {
      newErrors.guest_first_name = 'Guest first name is required';
    }
    if (!formData.guest_last_name) {
      newErrors.guest_last_name = 'Guest last name is required';
    }
    if (!formData.supplier) {
      newErrors.supplier = 'Supplier is required';
    }
    if (!formData.travel_date) {
      newErrors.travel_date = 'Travel date is required';
    }
    if (!formData.gross_commission || parseFloat(formData.gross_commission) <= 0) {
      newErrors.gross_commission = 'Valid gross commission amount is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await commissionsService.submit({
        ...formData,
        gross_commission: parseFloat(formData.gross_commission)
      });
      
      if (onSuccess) {
        onSuccess(response);
      }
      
      // Reset form
      setFormData({
        itinerary_id: '',
        first_name: user?.firstName || user?.first_name || '',
        last_name: user?.lastName || user?.last_name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        reservation_number: '',
        guest_first_name: '',
        guest_last_name: '',
        supplier: '',
        booking_date: new Date().toISOString().split('T')[0],
        travel_date: '',
        gross_commission: '',
        notes: ''
      });
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error submitting commission:', error);
      setErrors({ submit: error.response?.data?.error || 'Failed to submit commission' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Calculate agent commission preview
  const agentCommission = formData.gross_commission ? 
    (parseFloat(formData.gross_commission) * (user?.commissionPercentage || user?.commission_percentage || 70) / 100).toFixed(2) : 
    '0.00';

  return (
    <div className="commission-form-container">
      <div className="commission-form-header">
        <h2>Submit Commission Request</h2>
        {onClose && (
          <button 
            onClick={onClose} 
            className="close-button"
            type="button"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {errors.submit && (
        <div className="error-banner">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit} className="commission-form">
        {/* Agent Information Section */}
        <div className="form-section">
          <h3>Agent Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={formData.first_name}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                value={formData.last_name}
                readOnly
                className="readonly-input"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={formData.email}
                readOnly
                className="readonly-input"
              />
            </div>
            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                value={formData.phone}
                readOnly
                className="readonly-input"
              />
            </div>
          </div>
        </div>

        {/* Booking Information Section */}
        <div className="form-section">
          <h3>Booking Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Itinerary (Optional)</label>
              <select
                value={formData.itinerary_id}
                onChange={(e) => handleInputChange('itinerary_id', e.target.value)}
              >
                <option value="">Select an itinerary</option>
                {itineraries.map(itinerary => (
                  <option key={itinerary.id} value={itinerary.id}>
                    {itinerary.title} - {itinerary.client_name || 'Unknown Client'}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Reservation / Booking Number *</label>
              <input
                type="text"
                value={formData.reservation_number}
                onChange={(e) => handleInputChange('reservation_number', e.target.value)}
                placeholder="Enter reservation number"
                className={errors.reservation_number ? 'error' : ''}
              />
              {errors.reservation_number && <span className="error-text">{errors.reservation_number}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Supplier *</label>
              <select
                value={formData.supplier}
                onChange={(e) => handleInputChange('supplier', e.target.value)}
                className={errors.supplier ? 'error' : ''}
              >
                <option value="">Select a supplier</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.name}>
                    {supplier.name}
                  </option>
                ))}
              </select>
              {errors.supplier && <span className="error-text">{errors.supplier}</span>}
            </div>
            <div className="form-group">
              <label>Booking Date *</label>
              <input
                type="date"
                value={formData.booking_date}
                onChange={(e) => handleInputChange('booking_date', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Travel Date *</label>
              <input
                type="date"
                value={formData.travel_date}
                onChange={(e) => handleInputChange('travel_date', e.target.value)}
                className={errors.travel_date ? 'error' : ''}
              />
              {errors.travel_date && <span className="error-text">{errors.travel_date}</span>}
            </div>
          </div>
        </div>

        {/* Guest Information Section */}
        <div className="form-section">
          <h3>Guest Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Guest First Name *</label>
              <input
                type="text"
                value={formData.guest_first_name}
                onChange={(e) => handleInputChange('guest_first_name', e.target.value)}
                placeholder="Enter guest first name"
                className={errors.guest_first_name ? 'error' : ''}
              />
              {errors.guest_first_name && <span className="error-text">{errors.guest_first_name}</span>}
            </div>
            <div className="form-group">
              <label>Guest Last Name *</label>
              <input
                type="text"
                value={formData.guest_last_name}
                onChange={(e) => handleInputChange('guest_last_name', e.target.value)}
                placeholder="Enter guest last name"
                className={errors.guest_last_name ? 'error' : ''}
              />
              {errors.guest_last_name && <span className="error-text">{errors.guest_last_name}</span>}
            </div>
          </div>
        </div>

        {/* Commission Information Section */}
        <div className="form-section">
          <h3>Commission Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Gross Commission ($) *</label>
              <input
                type="number"
                step="0.01"
                value={formData.gross_commission}
                onChange={(e) => handleInputChange('gross_commission', e.target.value)}
                placeholder="0.00"
                className={errors.gross_commission ? 'error' : ''}
              />
              {errors.gross_commission && <span className="error-text">{errors.gross_commission}</span>}
            </div>
            <div className="form-group">
              <label>Agent Commission ({user?.commissionPercentage || user?.commission_percentage || 70}%)</label>
              <input
                type="text"
                value={`$${agentCommission}`}
                readOnly
                className="readonly-input calculated-field"
              />
              <span className="field-hint">Automatically calculated</span>
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div className="form-section">
          <h3>Additional Information</h3>
          <div className="form-group">
            <label>Notes (Optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Enter any additional notes or comments"
              rows="4"
            />
          </div>
        </div>

        <div className="form-actions">
          {onClose && (
            <button 
              type="button" 
              onClick={onClose} 
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
          )}
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Commission Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CommissionForm;
